export const clearDocsMeta = [
  { label: 'Role', value: 'Senior Product Designer' },
  { label: 'Client', value: 'US mortgage lender, via CI&T' },
  { label: 'Tools', value: 'Figma, Claude Design, Claude Code' },
  { label: 'Status', value: 'Queued for development' },
];

export type DocStatus = 'Needs review' | 'Approved' | 'Pending';

export const prototypeDocs: { name: string; uploaded: string; group: 'review' | 'inc'; status: DocStatus; final?: boolean }[] = [
  { name: 'Identified as: Bank Statement', uploaded: 'Uploaded 07/14 · 12:30 PM', group: 'review', status: 'Needs review' },
  { name: 'Identified as: Business License', uploaded: 'Uploaded 07/14 · 12:30 PM', group: 'review', status: 'Needs review' },
  { name: 'Identified as: Child Support', uploaded: 'Uploaded 07/14 · 12:30 PM', group: 'review', status: 'Needs review' },
  { name: '1099 (Filename123.pdf)', uploaded: 'Uploaded 07/12 · 9:10 AM', group: 'inc', status: 'Approved', final: true },
  { name: 'Gift Letter (Filename123.pdf)', uploaded: 'Uploaded 07/12 · 9:10 AM', group: 'inc', status: 'Pending' },
];

export const designVsLiveCalls = [
  {
    title: 'Hover previews',
    body: 'There is no hover on a phone, so document preview needed a redesign: a dedicated Preview tab in the viewer.',
  },
  {
    title: 'Advanced merge',
    body: "Live desktop only does a basic merge, so mobile doesn't get the richer version from the old design.",
  },
  {
    title: '“Send to LOS”',
    body: 'It exists in the live product but not in the design file. I treated it as system behavior, not something to reinterpret.',
  },
  {
    title: '“Needs review”',
    body: 'Its yellow treatment got lost on desktop. Mobile brings the emphasis back, so the documents waiting on the LO stand out.',
  },
];

export type AnalysisFlag = 'opportunity' | 'caution' | 'follow';

export const designVsLiveRows: { area: string; design: string; live: string; mobile: string; flag: AnalysisFlag }[] = [
  { area: 'Remove requested docs', design: 'Users could remove a doc after adding', live: 'No remove option available', mobile: 'Follow live behavior unless a change is approved', flag: 'follow' },
  { area: 'Indicator for new requests', design: 'Clear bar or highlight for new items', live: 'Exists, but weaker', mobile: 'Opportunity to clarify visually on mobile', flag: 'opportunity' },
  { area: 'Request description field', design: 'Larger, more prominent text area', live: 'Smaller, constrained', mobile: 'Careful hierarchy to avoid truncation', flag: 'caution' },
  { area: 'Contact / email selection', design: 'Contact picker', live: 'Free-text email only', mobile: 'Keyboard-friendly entry; picker not assumed', flag: 'follow' },
  { area: '“Needs review” styling', design: 'Intended to be yellow', live: 'Color treatment missing', mobile: 'Re-introduce the emphasis on mobile', flag: 'caution' },
  { area: 'Tag placement', design: 'Tags on the right side', live: 'Tags below the document', mobile: 'Mobile stacks anyway, so it aligns safely', flag: 'follow' },
  { area: 'Tag style', design: 'Intended tags, details unclear', live: 'Color-coded dots', mobile: 'Keep color, improve clarity for tap targets', flag: 'opportunity' },
  { area: 'Document preview', design: 'More explicit preview', live: 'Preview only on hover', mobile: 'No hover on mobile, so redesign required', flag: 'caution' },
  { area: 'Properties panel', design: 'Clean, intentional layout', live: 'Odd icons, spacing gaps', mobile: 'Safe to redesign the panel', flag: 'opportunity' },
  { area: 'Notes & annotations', design: 'Supported and expected', live: 'Works, but the UX is unclear', mobile: 'Simplified annotation flow', flag: 'opportunity' },
  { area: 'Split documents', design: 'Clear split interaction', live: 'Exists, but the UX differs', mobile: 'Simplify the interaction', flag: 'opportunity' },
  { area: 'Merge documents', design: 'Rich merge: reorder, combine', live: 'Very basic merge only', mobile: 'Do not design advanced merge', flag: 'follow' },
  { area: 'Sort & rearrange pages', design: 'Dedicated experience', live: 'Opens properties instead', mobile: 'Match live logic', flag: 'follow' },
  { area: 'Bulk actions', design: 'Designed, different layout', live: 'Works, looks different', mobile: 'Adapt the layout safely', flag: 'opportunity' },
  { area: '“Send to LOS” button', design: 'Not in the design', live: 'In the live UI', mobile: 'Treat as system behavior; don’t reinterpret', flag: 'follow' },
  { area: 'Drop zones', design: 'Managed drop zones', live: 'Smaller, simplified', mobile: 'Follow the simplified version', flag: 'follow' },
];

export const mobileScope = [
  'Review documents and filter by status',
  'Accept, reject or archive a document',
  'Categorize and tag documents',
  'Request documents in two steps, with auto reminders',
  'Upload from files or the camera',
];

export const desktopOnly = [
  'Advanced merge',
  'Page sorting and rearranging',
  'Managed drop zones',
  'Split and annotation flows, kept simple',
];

export const reviewFindings = [
  {
    title: 'Two systems, one word',
    body: '“Needs Review” is not a document status. Approving a document there touches three flags and never changes its status in the LOS.',
    highlight: false,
  },
  {
    title: 'A dropdown nobody used',
    body: 'Session data showed the status dropdown was clicked in about 7 sessions over 3 months, so it was safe to make status read-only.',
    highlight: false,
  },
  {
    title: 'Mostly misclicks',
    body: 'Most “Ready to ship” picks in that dropdown were misclicks by people aiming for “Reviewed.”',
    highlight: true,
  },
];

export const reviewDecided = [
  'The same buttons everywhere, always visible. Only the accept button’s name changes, by permission.',
  'No new statuses. Accept, Approve, Ready to Ship is the whole funnel.',
  'Reject marks a document Inactive and unlinks it from its condition. It never deletes it.',
  'Send to LOS becomes a checkbox next to the action buttons, so one click sets the status and sends.',
];

export const reviewCleanedUp = [
  'The status dropdown moves into a collapsible Details section, kept as an escape hatch.',
  'The For Review and Inactive checkboxes retire. Status already covers them.',
  'The Needs Review tag goes. Tags show only positive statuses.',
  'A guided flow from “Review docs”: Accept moves you to the next one, with a “doc 1 of N” count.',
];
